import { useRef, useState } from 'react';
import { useClient } from 'urql';
import { CheckCircle2, FileText, Loader2, Plus, Upload, XCircle } from 'lucide-react';
import PageHeader from '@/components/common/PageHeader.jsx';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import { extractInvoiceFields } from '@/ocr/index.js';
import { M_CREATE_PURCHASE } from '@/graphql/purchases.gql.js';
import { M_CREATE_PROVEEDOR, Q_PROVEEDORES } from '@/graphql/suppliers.gql.js';

const createId = () => (crypto?.randomUUID ? crypto.randomUUID() : `lote-${Date.now()}-${Math.random().toString(16).slice(2)}`);
const onlyDigits = value => String(value || '').replace(/\D/g, '');
const money = value => Number(value || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });

function makeRecord(file) {
  return { id: createId(), file, status: 'pendiente', fields: null, supplier: null, error: null };
}

function supplierName(supplier) {
  return supplier?.razon_social || supplier?.razonSocial || '';
}

export default function CargaMasivaCompras() {
  const client = useClient();
  const inputRef = useRef(null);
  const [records, setRecords] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [supplierRecord, setSupplierRecord] = useState(null);
  const [supplierDraft, setSupplierDraft] = useState(null);
  const [message, setMessage] = useState(null);

  const updateRecord = (id, patch) => setRecords(current => current.map(item => (item.id === id ? { ...item, ...patch } : item)));

  const openQuickSupplier = record => {
    setSupplierRecord(record);
    setSupplierDraft({
      razon_social: record.fields?.proveedor || '',
      cuit: record.fields?.cuit || '',
      condicion_iva: record.fields?.condicionIva || 'RI',
      contacto_email: record.fields?.email || '',
      contacto_tel: record.fields?.telefono || '',
      direccion: record.fields?.direccion || '',
    });
  };

  const findSupplier = async fields => {
    const search = fields.cuit || fields.proveedor;
    if (!search) return null;
    const result = await client.query(Q_PROVEEDORES, { filter: { search, estado: 'ACTIVO', limit: 10, offset: 0 } }, { requestPolicy: 'network-only' }).toPromise();
    if (result.error) throw new Error(result.error.message);
    const candidates = result.data?.proveedores?.items || [];
    const cuit = onlyDigits(fields.cuit);
    return candidates.find(item => cuit && onlyDigits(item.cuit) === cuit) || candidates.find(item => supplierName(item).toLowerCase() === String(fields.proveedor || '').toLowerCase()) || null;
  };

  const addFiles = event => {
    const files = Array.from(event.target.files || []);
    const invalid = files.filter(file => file.type !== 'application/pdf');
    const pdfs = files.filter(file => file.type === 'application/pdf');
    if (pdfs.length) setRecords(current => [...current, ...pdfs.map(makeRecord)]);
    if (invalid.length) setMessage('Solo se admiten comprobantes PDF en la carga masiva.');
    event.target.value = '';
  };

  const analyze = async () => {
    if (!records.length || processing) return;
    setProcessing(true);
    setMessage(null);
    for (const record of records) {
      if (record.status === 'guardada') continue;
      updateRecord(record.id, { status: 'analizando', error: null });
      try {
        const result = await extractInvoiceFields(record.file, { locale: 'es-AR' });
        const supplier = await findSupplier(result.fields);
        updateRecord(record.id, { fields: result.fields, supplier, status: supplier ? 'lista' : 'proveedor_pendiente' });
      } catch (error) {
        updateRecord(record.id, { status: 'error', error: error.message || 'No se pudo analizar el PDF.' });
      }
    }
    setProcessing(false);
  };

  const createQuickSupplier = async () => {
    if (!supplierDraft?.razon_social || onlyDigits(supplierDraft.cuit).length !== 11) {
      setMessage('No se puede crear el proveedor: el OCR debe detectar razón social y CUIT válido.');
      return;
    }
    const input = {
      razon_social: supplierDraft.razon_social.trim(),
      cuit: supplierDraft.cuit,
      condicion_iva: supplierDraft.condicion_iva,
      contacto_tel: supplierDraft.contacto_tel || undefined,
      contacto_email: supplierDraft.contacto_email || undefined,
      direccion: supplierDraft.direccion || undefined,
      observaciones: 'Alta rápida desde carga masiva de factura OCR. Revisar y completar datos pendientes.',
      estado: 'ACTIVO',
      alta_rapida_ocr: true,
    };
    const response = await client.mutation(M_CREATE_PROVEEDOR, { input }).toPromise();
    if (response.error) {
      setMessage(response.error.message || 'No se pudo crear el proveedor.');
      return;
    }
    const created = response.data?.proveedorCreate;
    if (created && supplierRecord) updateRecord(supplierRecord.id, { supplier: created, status: 'lista' });
    setSupplierRecord(null);
    setSupplierDraft(null);
  };

  const saveReady = async () => {
    const ready = records.filter(record => record.status === 'lista' && record.supplier?.id);
    if (!ready.length || saving) return;
    setSaving(true);
    setMessage(null);
    for (const record of ready) {
      const fields = record.fields || {};
      const input = {
        supplierId: record.supplier.id,
        tipo: fields.tipo || 'Factura A',
        puntoVenta: fields.puntoVenta || '0000',
        numero: fields.numero || `OCR-${record.id.slice(0, 8)}`,
        fecha: fields.fecha || new Date().toISOString().slice(0, 10),
        cae: fields.cae || null,
        caeVto: fields.caeVto || null,
        moneda: 'ARS',
        condicionCompra: 'Contado',
        importes: {
          neto: Number(fields.neto || 0), iva21: Number(fields.iva21 || 0), iva105: Number(fields.iva105 || 0),
          iva27: Number(fields.iva27 || 0), exento: Number(fields.exento || 0), noGravado: Number(fields.noGravado || 0),
          percepIva: Number(fields.percepIva || 0), percepIibb: Number(fields.percepIibb || 0), otros: Number(fields.otros || 0), total: Number(fields.total || 0),
        },
        items: [],
        adjuntos: [{ filename: record.file.name, mimeType: record.file.type, size: record.file.size }],
        observaciones: 'Generada mediante carga masiva con OCR. Verificar importes antes de contabilizar.',
      };
      updateRecord(record.id, { status: 'guardando' });
      const response = await client.mutation(M_CREATE_PURCHASE, { input }).toPromise();
      if (response.error) updateRecord(record.id, { status: 'error', error: response.error.message });
      else updateRecord(record.id, { status: 'guardada' });
    }
    setSaving(false);
    setMessage('Se terminó el registro de las facturas listas. Revisá las que quedaron con error o proveedor pendiente.');
  };

  const readyCount = records.filter(item => item.status === 'lista').length;
  const savedCount = records.filter(item => item.status === 'guardada').length;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Carga masiva de facturas"
        description="Subí varios PDFs, analizalos con OCR y registrá cada compra después de confirmar el proveedor."
        breadcrumbs={[{ label: 'Tesorería', href: '/tesoreria' }, { label: 'Registro de compras', href: '/tesoreria/compras' }, { label: 'Carga masiva' }]}
      />

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Comprobantes PDF</h2>
            <p className="mt-1 text-sm text-[rgba(var(--muted),0.85)]">El OCR lee todas las páginas de cada archivo. Las facturas se guardan solo cuando el proveedor esté confirmado.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--brand),0.35)] bg-[rgba(var(--brand),0.12)] px-4 py-2 text-sm font-semibold text-[rgb(var(--brand))]">
              <Upload className="h-4 w-4" /> Agregar PDFs
            </button>
            <button type="button" disabled={!records.length || processing} onClick={analyze} className="inline-flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Analizar lote
            </button>
            <button type="button" disabled={!readyCount || saving} onClick={saveReady} className="inline-flex items-center gap-2 rounded-xl bg-[rgb(var(--brand-700))] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Registrar {readyCount} lista{readyCount === 1 ? '' : 's'}
            </button>
          </div>
        </div>
        <input ref={inputRef} type="file" accept="application/pdf" multiple onChange={addFiles} className="sr-only" />
        {message ? <p className="mt-4 rounded-xl bg-[rgba(var(--brand),0.1)] px-4 py-3 text-sm text-[rgb(var(--brand-700))]">{message}</p> : null}
      </section>

      {records.length ? (
        <section className="overflow-hidden rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
          <div className="border-b border-[rgba(var(--border),0.45)] px-6 py-4 text-sm text-[rgba(var(--muted),0.85)]">{records.length} archivos · {readyCount} listos · {savedCount} registrados</div>
          <ul className="divide-y divide-[rgba(var(--border),0.35)]">
            {records.map(record => (
              <li key={record.id} className="flex flex-col gap-4 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-[rgb(var(--fg))]">{record.file.name}</p>
                  <p className="mt-1 text-xs text-[rgba(var(--muted),0.8)]">{(record.file.size / 1024).toFixed(0)} KB · {record.fields?.tipo || 'Pendiente de OCR'} · {record.fields?.total != null ? money(record.fields.total) : 'Sin total detectado'}</p>
                  {record.fields ? <p className="mt-1 text-sm text-[rgba(var(--muted),0.9)]">Proveedor detectado: {record.fields.proveedor || 'No detectado'} {record.fields.cuit ? `· CUIT ${record.fields.cuit}` : ''}</p> : null}
                  {record.error ? <p className="mt-1 text-sm text-red-600">{record.error}</p> : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {record.status === 'analizando' || record.status === 'guardando' ? <span className="inline-flex items-center gap-2 text-sm text-[rgba(var(--muted),0.9)]"><Loader2 className="h-4 w-4 animate-spin" /> {record.status === 'analizando' ? 'Analizando…' : 'Guardando…'}</span> : null}
                  {record.status === 'lista' ? <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">Proveedor: {supplierName(record.supplier)}</span> : null}
                  {record.status === 'guardada' ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800"><CheckCircle2 className="h-3.5 w-3.5" /> Registrada</span> : null}
                  {record.status === 'proveedor_pendiente' ? <><span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800"><XCircle className="h-3.5 w-3.5" /> Proveedor no existe</span><button type="button" onClick={() => openQuickSupplier(record)} className="inline-flex items-center gap-1 rounded-xl bg-[rgb(var(--brand))] px-3 py-2 text-xs font-semibold text-white"><Plus className="h-3.5 w-3.5" /> Revisar y dar de alta</button></> : null}
                  {record.status === 'pendiente' || record.status === 'error' ? <button type="button" onClick={() => updateRecord(record.id, { status: 'pendiente', error: null })} className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))]">Reintentar</button> : null}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <ModalBase isOpen={Boolean(supplierRecord)} onClose={() => setSupplierRecord(null)} labelledBy="proveedor-ocr-modal">
        <div className="space-y-5">
          {supplierDraft ? <div className="grid grid-cols-1 gap-3 rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.55)] p-4 md:grid-cols-2">
            <label className="text-sm font-semibold text-[rgb(var(--fg))] md:col-span-2">Razón social<input value={supplierDraft.razon_social} onChange={event => setSupplierDraft(current => ({ ...current, razon_social: event.target.value }))} className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-2 text-sm font-normal" /></label>
            <label className="text-sm font-semibold text-[rgb(var(--fg))]">CUIT<input value={supplierDraft.cuit} onChange={event => setSupplierDraft(current => ({ ...current, cuit: event.target.value }))} className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-2 text-sm font-normal" /></label>
            <label className="text-sm font-semibold text-[rgb(var(--fg))]">Condición IVA<select value={supplierDraft.condicion_iva} onChange={event => setSupplierDraft(current => ({ ...current, condicion_iva: event.target.value }))} className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-2 text-sm font-normal"><option value="RI">Responsable inscripto</option><option value="MT">Monotributo</option><option value="EX">Exento</option><option value="CF">Consumidor final</option><option value="RNI">Responsable no inscripto</option></select></label>
            <label className="text-sm font-semibold text-[rgb(var(--fg))]">Email<input type="email" value={supplierDraft.contacto_email} onChange={event => setSupplierDraft(current => ({ ...current, contacto_email: event.target.value }))} className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-2 text-sm font-normal" /></label>
            <label className="text-sm font-semibold text-[rgb(var(--fg))]">Teléfono<input value={supplierDraft.contacto_tel} onChange={event => setSupplierDraft(current => ({ ...current, contacto_tel: event.target.value }))} className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-2 text-sm font-normal" /></label>
            <label className="text-sm font-semibold text-[rgb(var(--fg))] md:col-span-2">Dirección<input value={supplierDraft.direccion} onChange={event => setSupplierDraft(current => ({ ...current, direccion: event.target.value }))} className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-2 text-sm font-normal" /></label>
          </div> : null}
          <header><h2 id="proveedor-ocr-modal" className="text-lg font-semibold text-[rgb(var(--fg))]">Agregar proveedor detectado</h2><p className="mt-1 text-sm text-[rgba(var(--muted),0.85)]">Confirmá y completá los datos obligatorios. Al guardar, quedará asignado a esta factura.</p></header>
          {supplierRecord ? <div className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.55)] p-4 text-sm text-[rgb(var(--fg))]"><p><strong>Razón social:</strong> {supplierRecord.fields?.proveedor || 'No detectada'}</p><p className="mt-2"><strong>CUIT:</strong> {supplierRecord.fields?.cuit || 'No detectado'}</p><div className="mt-5 flex justify-end gap-3"><button type="button" onClick={() => setSupplierRecord(null)} className="rounded-xl border border-[rgba(var(--border),0.45)] px-4 py-2 text-sm font-semibold">Cancelar</button><button type="button" onClick={createQuickSupplier} className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white">Crear y asignar</button></div></div> : null}
        </div>
      </ModalBase>
    </div>
  );
}
